import { memo, useState, useEffect } from "react";
import type { GlobalNotificationT } from "../../features/NewPrices2024/models/models";
import { useAuth } from "../../utils/hooks/useAuth";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentLang } from "../../store/selectors/appSelectors";
import GlobalNotificationCampaignContent from "./components/GlobalNotificationCampaignContent";
import { isLanguageMatched } from "../../features/NewPrices2024/utils/notificationHelpers";

interface GlobalNotificationCampaignBannerI {
  notification: GlobalNotificationT;
}

/**
 * Custom banner component for Campaign notifications.
 * Displays after sidebar wrapper, not as a popup.
 * Hidden for current session if user closes it (using sessionStorage).
 */
const GlobalNotificationCampaignBanner = memo(
  (props: GlobalNotificationCampaignBannerI) => {
    const { notification } = props;
    const { currentUser } = useAuth();
    const currentLang = useAppSelector(selectCurrentLang);
    const [isVisible, setIsVisible] = useState(true);

    // Check sessionStorage on mount
    useEffect(() => {
      if (currentUser?.id) {
        const sessionKey = `campaign-banner-closed-${notification.TitleConst}-${currentUser.id}`;
        const isClosed = sessionStorage.getItem(sessionKey);
        if (isClosed === "true") {
          setIsVisible(false);
        }
      }
    }, [currentUser?.id, notification.TitleConst]);

    // Show only if current language matches notification Languages
    // Languages is required field, so it will always be present
    if (!notification.Languages || !isLanguageMatched(currentLang, notification.Languages)) {
      return null;
    }

    const handleClose = () => {
      if (currentUser?.id) {
        const sessionKey = `campaign-banner-closed-${notification.TitleConst}-${currentUser.id}`;
        sessionStorage.setItem(sessionKey, "true");
      }
      setIsVisible(false);
    };

    if (!isVisible) {
      return null;
    }

    return (
      <GlobalNotificationCampaignContent
        notification={notification}
        onClose={handleClose}
      />
    );
  }
);

export default GlobalNotificationCampaignBanner;

